const DISABLED_LANDMARKS = new Set([
  0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10,     // 얼굴 전체 (코, 눈, 귀, 입)
  17, 18, 19, 20, 21, 22,               // 손가락 전체 (새끼, 검지, 엄지)
]);

/**
 * 평행한 오프셋 선을 그리는 헬퍼 함수
 */
function drawOffsetLine(ctx, p1, p2, width, height, color, glow, offset = 0, lineWidth = 3) {
  const x1 = p1.x * width;
  const y1 = p1.y * height;
  const x2 = p2.x * width;
  const y2 = p2.y * height;

  const dx = x2 - x1;
  const dy = y2 - y1;
  const len = Math.hypot(dx, dy);
  if (len === 0) return;

  const nx = (-dy / len) * offset;
  const ny = (dx / len) * offset;

  ctx.beginPath();
  ctx.lineWidth = lineWidth;
  ctx.strokeStyle = color;
  ctx.shadowColor = glow || 'transparent';
  ctx.shadowBlur = glow !== 'transparent' ? 8 : 0;

  ctx.moveTo(x1 + nx, y1 + ny);
  ctx.lineTo(x2 + nx, y2 + ny);
  ctx.stroke();
}

/**
 * 2D 캔버스에 양궁 포즈 스켈레톤과 관절점을 렌더링합니다.
 * @param {CanvasRenderingContext2D} ctx - 캔버스 2D 컨텍스트
 * @param {Array} landmarks - 33개 랜드마크 배열
 * @param {number} width - 캔버스 너비
 * @param {number} height - 캔버스 높이
 * @param {string} dominantHand - 'right' (기본값) | 'left'
 */
export function drawPoseSkeleton(ctx, landmarks, width, height, dominantHand = 'right') {
  if (!ctx || !landmarks || landmarks.length === 0) return;

  ctx.save();
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';

  const isVis = (idx) => landmarks[idx] && (landmarks[idx].visibility ?? 1) > 0.4;
  const isLeftHanded = dominantHand === 'left';

  // -------------------------------------------------------------
  // 1. 단일 연결선 (몸통 및 하체)
  // -------------------------------------------------------------
  const supportShoulderIdx = isLeftHanded ? 12 : 11;
  const supportHipIdx = isLeftHanded ? 24 : 23;
  const drawShoulderIdx = isLeftHanded ? 11 : 12;
  const drawHipIdx = isLeftHanded ? 23 : 24;

  const singleLines = [
    // 어깨 및 골반 수평선 (퍼플)
    { start: 11, end: 12, color: '#c084fc', glow: 'rgba(192, 132, 252, 0.5)', width: 3 },
    { start: 23, end: 24, color: '#c084fc', glow: 'rgba(192, 132, 252, 0.5)', width: 3 },

    // 받침팔 몸통선 (시안)
    { start: supportShoulderIdx, end: supportHipIdx, color: '#38bdf8', glow: 'rgba(56, 189, 248, 0.6)', width: 3.5 },
    // 당김팔 몸통선 (오렌지)
    { start: drawShoulderIdx, end: drawHipIdx, color: '#f97316', glow: 'rgba(249, 115, 22, 0.6)', width: 3.5 },

    // 하체 라인 (슬레이트)
    { start: 23, end: 25, color: 'rgba(148, 163, 184, 0.45)', width: 2.5 },
    { start: 24, end: 26, color: 'rgba(148, 163, 184, 0.45)', width: 2.5 },
    { start: 25, end: 27, color: 'rgba(148, 163, 184, 0.45)', width: 2.5 },
    { start: 26, end: 28, color: 'rgba(148, 163, 184, 0.45)', width: 2.5 },
    { start: 27, end: 29, color: 'rgba(148, 163, 184, 0.45)', width: 2 },
    { start: 28, end: 30, color: 'rgba(148, 163, 184, 0.45)', width: 2 },
    { start: 29, end: 31, color: 'rgba(148, 163, 184, 0.45)', width: 2 },
    { start: 30, end: 32, color: 'rgba(148, 163, 184, 0.45)', width: 2 },
    { start: 27, end: 31, color: 'rgba(148, 163, 184, 0.45)', width: 2 },
    { start: 28, end: 32, color: 'rgba(148, 163, 184, 0.45)', width: 2 },
  ];

  for (const conn of singleLines) {
    if (isVis(conn.start) && isVis(conn.end)) {
      drawOffsetLine(
        ctx,
        landmarks[conn.start],
        landmarks[conn.end],
        width,
        height,
        conn.color,
        conn.glow,
        0,
        conn.width
      );
    }
  }

  // -------------------------------------------------------------
  // 2. 팔 부위 렌더링 (어깨-팔꿈치는 2줄, 팔꿈치-손목은 단독 1줄)
  // -------------------------------------------------------------
  const supSh = isLeftHanded ? 12 : 11;
  const supEl = isLeftHanded ? 14 : 13;
  const supWr = isLeftHanded ? 16 : 15;

  const drwSh = isLeftHanded ? 11 : 12;
  const drwEl = isLeftHanded ? 13 : 14;
  const drwWr = isLeftHanded ? 15 : 16;

  // [받침팔 (시안 들림각 + 에메랄드 지탱각)]
  if (isVis(supSh) && isVis(supEl)) {
    drawOffsetLine(ctx, landmarks[supSh], landmarks[supEl], width, height, '#38bdf8', 'rgba(56, 189, 248, 0.8)', -2.5, 3.5);
    drawOffsetLine(ctx, landmarks[supSh], landmarks[supEl], width, height, '#34d399', 'rgba(52, 211, 153, 0.8)', 2.5, 3.5);
  }
  if (isVis(supEl) && isVis(supWr)) {
    drawOffsetLine(ctx, landmarks[supEl], landmarks[supWr], width, height, '#34d399', 'rgba(52, 211, 153, 0.8)', 0, 3.5);
  }

  // [당김팔 (오렌지 들림각 + 골드 앵커각)]
  if (isVis(drwSh) && isVis(drwEl)) {
    drawOffsetLine(ctx, landmarks[drwSh], landmarks[drwEl], width, height, '#f97316', 'rgba(249, 115, 22, 0.8)', -2.5, 3.5);
    drawOffsetLine(ctx, landmarks[drwSh], landmarks[drwEl], width, height, '#fbbf24', 'rgba(251, 191, 36, 0.8)', 2.5, 3.5);
  }
  if (isVis(drwEl) && isVis(drwWr)) {
    drawOffsetLine(ctx, landmarks[drwEl], landmarks[drwWr], width, height, '#fbbf24', 'rgba(251, 191, 36, 0.8)', 0, 3.5);
  }

  // -------------------------------------------------------------
  // 3. 관절점(Landmarks) 렌더링
  // -------------------------------------------------------------
  for (let i = 0; i < landmarks.length; i++) {
    if (DISABLED_LANDMARKS.has(i)) continue;
    const pt = landmarks[i];
    if (pt && (pt.visibility ?? 1) > 0.4) {
      const x = pt.x * width;
      const y = pt.y * height;

      let outerColor = '#94a3b8';
      let glowColor = 'rgba(148, 163, 184, 0.4)';

      if (i === supSh || i === supEl || i === supWr || i === supportHipIdx) {
        // 받침팔 관절 (시안)
        outerColor = '#38bdf8';
        glowColor = '#0284c7';
      } else if (i === drwSh || i === drwEl || i === drawHipIdx) {
        // 당김팔 관절 (오렌지)
        outerColor = '#f97316';
        glowColor = '#ea580c';
      } else if (i === drwWr) {
        // 앵커 손목 (골드)
        outerColor = '#fbbf24';
        glowColor = '#d97706';
      }

      // 외곽 포인트 링
      ctx.beginPath();
      ctx.arc(x, y, 6, 0, 2 * Math.PI);
      ctx.fillStyle = outerColor;
      ctx.shadowColor = glowColor;
      ctx.shadowBlur = 10;
      ctx.fill();

      // 내부 코어 점
      ctx.beginPath();
      ctx.arc(x, y, 2.5, 0, 2 * Math.PI);
      ctx.fillStyle = '#0f172a';
      ctx.shadowBlur = 0;
      ctx.fill();
    }
  }

  ctx.restore();
}
